import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Input, Text, Field, Button, CloseButton, Textarea } from '@chakra-ui/react'
import { colors } from '../../../../utilities/colors'
import useGooglePlaces from '../../../../hooks/useGooglePlaces'
import AutocompleteDropdown from '../../../common/AutocompleteDropdown'
import { locationTypeLabels } from '../../../../constants/carpoolLocation'

const inputStyles = {
  bgColor: colors.grey900,
  borderColor: colors.grey700,
  color: colors.grey100,
  _focus: { borderColor: colors.grey100, ringColor: colors.grey100 },
}

const errorInputStyles = {
  bgColor: colors.grey900,
  borderColor: colors.fpxRed,
  color: colors.grey100,
  _focus: { borderColor: colors.fpxRed, ringColor: colors.fpxRed },
}

export default function OrderLocationFields({
  label,
  quoteLocation,
  locationType,
  values,
  onChange,
  onClear,
  onCancel,
  accentColor,
  errors = {},
  notesLabel,
}) {
  const { t } = useTranslation()
  const resolvedNotesLabel = notesLabel || t('Delivery Instructions')
  const { getPlacePredictions, getAddressPredictions, resolvePlace } = useGooglePlaces()

  const locationHint = [quoteLocation.city, quoteLocation.state, quoteLocation.postal_code].filter(Boolean).join(', ')
  const hasValues = Object.values(values).some((v) => v)

  const update = (field, val) => {
    onChange({ ...values, [field]: val })
  }

  const fetchPlaces = useCallback(
    (input) => getPlacePredictions(input, locationHint),
    [getPlacePredictions, locationHint],
  )

  const fetchAddresses = useCallback(
    (input) => getAddressPredictions(input, locationHint),
    [getAddressPredictions, locationHint],
  )

  const handleNameSelect = async (prediction) => {
    const place = await resolvePlace(prediction.placeId)
    if (place) {
      onChange({
        ...values,
        name: place.name || values.name,
        address: place.address || values.address,
        address_2: place.address_2 || values.address_2,
      })
    }
  }

  const handleAddressSelect = async (prediction) => {
    const place = await resolvePlace(prediction.placeId)
    if (place) {
      onChange({
        ...values,
        address: place.address || values.address,
        address_2: place.address_2 || values.address_2,
      })
    }
  }

  return (
    <Box
      w="full"
      minW={0}
      p={{ base: 3, md: 4 }}
      borderLeft="4px solid"
      borderLeftColor={accentColor}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3} flexWrap="wrap">
        <Text fontSize="md" fontWeight="semibold" color={accentColor} minW={0} flex={1}>
          {label}
          <Text as="span" fontSize="sm" fontWeight="normal" color={colors.grey300} ml={2}>
            {locationHint}
          </Text>
        </Text>
        {onCancel && (
          <CloseButton
            size="sm"
            color={colors.grey300}
            _hover={{ color: colors.grey100, bg: colors.grey700 }}
            onClick={onCancel}
            aria-label={t('Cancel')}
          />
        )}
      </Box>

      {locationType && (
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={3}>
          <Text fontSize="sm" color={colors.grey300}>{t('Location Type')}</Text>
          <Text fontSize="sm" color={colors.grey100} fontWeight="medium">
            {t(locationTypeLabels[locationType] || locationType)}
          </Text>
        </Box>
      )}

      {onClear && hasValues && (
        <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
          <Button
            size="xs"
            variant="outline"
            color={colors.fpxRed}
            borderColor={colors.fpxRed}
            _hover={{ bg: colors.fpxRed, color: colors.grey100 }}
            onClick={onClear}
          >
            {t('Clear')}
          </Button>
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap={3}>
        <Field.Root invalid={!!errors.name}>
          <Field.Label fontSize="sm" color={colors.grey300}>{t('Location Name')}</Field.Label>
          <AutocompleteDropdown
            value={values.name}
            onChange={(e) => update('name', e.target.value)}
            onSelect={handleNameSelect}
            fetchOptions={fetchPlaces}
            getOptionKey={(p) => p.placeId}
            getOptionLabel={(p) => p.text?.text}
            inputProps={errors.name ? errorInputStyles : inputStyles}
          />
          {errors.name && (
            <Field.ErrorText fontSize="xs" color={colors.fpxRed}>{errors.name}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root invalid={!!errors.address}>
          <Field.Label fontSize="sm" color={colors.grey300}>{t('Address')}</Field.Label>
          <AutocompleteDropdown
            value={values.address}
            onChange={(e) => update('address', e.target.value)}
            onSelect={handleAddressSelect}
            fetchOptions={fetchAddresses}
            getOptionKey={(p) => p.placeId}
            getOptionLabel={(p) => p.text?.text}
            inputProps={errors.address ? errorInputStyles : inputStyles}
          />
          {errors.address && (
            <Field.ErrorText fontSize="xs" color={colors.fpxRed}>{errors.address}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label fontSize="sm" color={colors.grey300}>{t('Address Line 2')}</Field.Label>
          <Input value={values.address_2} onChange={(e) => update('address_2', e.target.value)} {...inputStyles} />
        </Field.Root>

        <Box display="flex" flexDirection={{ base: 'column', md: 'row' }} gap={3}>
          <Field.Root flex={1} invalid={!!errors.contact_name}>
            <Field.Label fontSize="sm" color={colors.grey300}>{t('Contact Name')}</Field.Label>
            <Input
              value={values.contact_name}
              onChange={(e) => update('contact_name', e.target.value)}
              {...(errors.contact_name ? errorInputStyles : inputStyles)}
            />
            {errors.contact_name && (
              <Field.ErrorText fontSize="xs" color={colors.fpxRed}>{errors.contact_name}</Field.ErrorText>
            )}
          </Field.Root>
          <Field.Root flex={1} invalid={!!errors.contact_phone}>
            <Field.Label fontSize="sm" color={colors.grey300}>{t('Phone')}</Field.Label>
            <Input
              value={values.contact_phone}
              onChange={(e) => update('contact_phone', e.target.value)}
              {...(errors.contact_phone ? errorInputStyles : inputStyles)}
            />
            {errors.contact_phone && (
              <Field.ErrorText fontSize="xs" color={colors.fpxRed}>{errors.contact_phone}</Field.ErrorText>
            )}
          </Field.Root>
          <Field.Root flex={1} invalid={!!errors.contact_email}>
            <Field.Label fontSize="sm" color={colors.grey300}>{t('Email')}</Field.Label>
            <Input
              value={values.contact_email}
              onChange={(e) => update('contact_email', e.target.value)}
              type="email"
              {...(errors.contact_email ? errorInputStyles : inputStyles)}
            />
            {errors.contact_email && (
              <Field.ErrorText fontSize="xs" color={colors.fpxRed}>{errors.contact_email}</Field.ErrorText>
            )}
          </Field.Root>
        </Box>

        <Field.Root>
          <Field.Label fontSize="sm" color={colors.grey300}>
            {resolvedNotesLabel}{' '}
            <Text as="span" fontSize="xs" color={colors.grey300} fontWeight="normal">
              ({t('PIN, gate codes, parking, hours, etc.')})
            </Text>
          </Field.Label>
          <Textarea
            value={values.external_notes || ''}
            onChange={(e) => update('external_notes', e.target.value)}
            placeholder={t('e.g. Gate code 4821, park in back lot, ask for Mike')}
            rows={3}
            maxLength={255}
            {...inputStyles}
          />
          <Text fontSize="xs" color={colors.grey300} alignSelf="flex-end" mt={1}>
            {(values.external_notes || '').length}/255
          </Text>
        </Field.Root>
      </Box>
    </Box>
  )
}
